import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiOutlineLocationMarker, HiOutlineCalendar, HiX, HiCheck, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import './styles/AProposManagement.css';
import './styles/Modal.css';
import { aproposService } from '../../services/aproposService';
import { speakersService } from '../../services/speakersService';
import { supabase } from '../../supabaseClient';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import MultiLangInput from './MultiLangInput';
import ExpandableText from './ExpandableText';

function AProposManagement({ selectedEdition }) {
  const [aproposData, setAproposData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalMode, setDetailModalMode] = useState('add');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('president');

  const SECTIONS = [
    { key: 'president', label: 'Président', flag: 'is_president', orderCol: 'order_president' },
    { key: 'scientific', label: 'Scientifique', flag: 'is_scientific', orderCol: 'order_scientific' },
    { key: 'honorary', label: 'Honneur', flag: 'is_honorary', orderCol: 'order_honorary' },
    { key: 'organizing', label: 'Organisation', flag: 'is_organizing', orderCol: 'order_organizing' },
  ];

  const iconOptions = [
    { value: 'HiOutlineLocationMarker', label: 'Location', component: <HiOutlineLocationMarker /> },
    { value: 'HiOutlineCalendar', label: 'Calendar', component: <HiOutlineCalendar /> }
  ];

  // Load data from Supabase
  useEffect(() => {
    if (selectedEdition) {
      loadAproposData();
    }
  }, [selectedEdition]);

  const loadAproposData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      const data = await aproposService.getAproposData(selectedEdition);
      
      // Check if database is empty - more thorough check
      const isEmpty = (!data.pageHeader?.title || data.pageHeader.title.trim() === '') && 
                     (!data.eventIntro?.paragraph1 || data.eventIntro.paragraph1.trim() === '') && 
                     (!data.eventIntro?.paragraph2 || data.eventIntro.paragraph2.trim() === '') && 
                     (!data.committeeSection?.title || data.committeeSection.title.trim() === '') &&
                     (!data.eventDetails || data.eventDetails.length === 0) &&
                     (!data.committeeMembers || data.committeeMembers.length === 0);
      
      if (isEmpty) {
        console.log('Database appears empty, setting default data');
        setIsDatabaseEmpty(true);
        // Set default data for editing
        setAproposData({
          pageHeader: { title: '' },
          eventIntro: { paragraph1: '', paragraph2: '' },
          images: { image1_url: '', image2_url: '', image3_url: '' },
          committeeSection: { title: '' },
          eventDetails: [],
          committeeMembers: []
        });
      } else {
        console.log('Data found:', data);
        setAproposData(data);
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading apropos data:', error);
      // If there's an error, assume database might be empty or there's no data for this edition
      setIsDatabaseEmpty(true);
      // Set default data for editing
      setAproposData({
        pageHeader: { title: '' },
        eventIntro: { paragraph1: '', paragraph2: '' },
        images: { image1_url: '', image2_url: '', image3_url: '' },
        committeeSection: { title: '' },
        eventDetails: [],
        committeeMembers: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteMember = (id) => {
    handleDeleteCommitteeMember(id);
  };

  const handleAddMember = () => {
    setSelectedMember(null);
    setModalMode('add');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
    setModalMode('add');
  };

  const handleEditDetail = (detail) => {
    setSelectedDetail(detail);
    setDetailModalMode('edit');
    setShowDetailModal(true);
  };

  const handleDeleteDetail = (id) => {
    handleDeleteEventDetail(id);
  };

  const handleAddDetail = () => {
    setSelectedDetail(null);
    setDetailModalMode('add');
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDetail(null);
    setDetailModalMode('add');
  };

  const handleUpdatePageHeader = async (title) => {
    if (isDatabaseEmpty) {
      // Just update local state if database is empty
      setAproposData(prev => ({
        ...prev,
        pageHeader: { ...prev.pageHeader, title }
      }));
      setHasUnsavedChanges(true);
      return;
    }
    
    // Update local state only, no automatic save
    setAproposData(prev => ({
      ...prev,
      pageHeader: { ...prev.pageHeader, title }
    }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateEventIntro = async (field, value) => {
    if (isDatabaseEmpty) {
      // Just update local state if database is empty
      setAproposData(prev => ({
        ...prev,
        eventIntro: { ...prev.eventIntro, [field]: value }
      }));
      setHasUnsavedChanges(true);
      return;
    }
    
    // Update local state only, no automatic save
    setAproposData(prev => ({
      ...prev,
      eventIntro: { ...prev.eventIntro, [field]: value }
    }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateCommitteeSection = async (title) => {
    if (isDatabaseEmpty) {
      // Just update local state if database is empty
      setAproposData(prev => ({
        ...prev,
        committeeSection: { ...prev.committeeSection, title }
      }));
      setHasUnsavedChanges(true);
      return;
    }
    
    // Update local state only, no automatic save
    setAproposData(prev => ({
      ...prev,
      committeeSection: { ...prev.committeeSection, title }
    }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateImageTitle = (imageKey, title) => {
    setAproposData(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [imageKey]: title
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveMainData = async () => {
    try {
      await aproposService.updateAproposData(aproposData, selectedEdition);
      setHasUnsavedChanges(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving main data:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
  };

  const handleSaveToDatabase = async () => {
    try {
      // Save main apropos data
      await aproposService.updateAproposData(aproposData, selectedEdition);
      
      // Save event details if any
      for (const detail of aproposData.eventDetails || []) {
        if (!detail.id || detail.id.toString().startsWith('temp_')) {
          await aproposService.addEventDetail(detail, selectedEdition);
        }
      }
      
      // Save committee members if any
      for (const member of aproposData.committeeMembers || []) {
        if (!member.id || member.id.toString().startsWith('temp_')) {
          await aproposService.addCommitteeMember(member, selectedEdition);
        }
      }
      
      setIsDatabaseEmpty(false);
      setHasUnsavedChanges(false);
      showSuccessMessage();
      
      // Reload data to get proper IDs from database
      setTimeout(() => {
        loadAproposData();
      }, 1000);
      
    } catch (error) {
      console.error('Error saving to database:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
  };

  const handleAddEventDetail = async (detailData) => {
    try {
      if (isDatabaseEmpty) {
        // Add to local state with temporary ID
        const tempDetail = {
          ...detailData,
          id: `temp_${Date.now()}`
        };
        setAproposData(prev => ({
          ...prev,
          eventDetails: [...(prev.eventDetails || []), tempDetail]
        }));
        setHasUnsavedChanges(true);
        return;
      }
      
      const newDetail = await aproposService.addEventDetail(detailData, selectedEdition);
      setAproposData(prev => ({
        ...prev,
        eventDetails: [...(prev.eventDetails || []), {
          id: newDetail.id,
          title: newDetail.title,
          content: newDetail.content,
          icon: newDetail.icon
        }]
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error adding event detail:', error);
    }
  };

  const handleAddCommitteeMember = async (memberData) => {
    try {
      if (isDatabaseEmpty) {
        // Add to local state with temporary ID and proper display_order
        const tempMember = {
          ...memberData,
          id: `temp_${Date.now()}`,
          display_order: (aproposData.committeeMembers || []).length
        };
        setAproposData(prev => ({
          ...prev,
          committeeMembers: [...(prev.committeeMembers || []), tempMember]
        }));
        setHasUnsavedChanges(true);
        return;
      }
      
      // Add display_order to memberData
      const memberWithOrder = {
        ...memberData,
        display_order: (aproposData.committeeMembers || []).length
      };
      
      const newMember = await aproposService.addCommitteeMember(memberWithOrder, selectedEdition);
      setAproposData(prev => ({
        ...prev,
        committeeMembers: [...(prev.committeeMembers || []), {
          id: newMember.id,
          name: newMember.name,
          title: newMember.title,
          organization: newMember.organization,
           image: newMember.image_url || newMember.image_path,
           logo: newMember.company_logo_url,
           is_honorary: newMember.is_honorary,
           is_organizing: newMember.is_organizing,
           is_president: newMember.is_president,
           display_order: newMember.display_order
         }]
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error adding committee member:', error);
    }
  };

  const handleUpdateEventDetail = async (detailData) => {
    try {
      await aproposService.updateEventDetail(detailData);
      setAproposData(prev => ({
        ...prev,
        eventDetails: prev.eventDetails.map(detail =>
          detail.id === detailData.id ? { ...detail, ...detailData } : detail
        )
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating event detail:', error);
    }
  };

  const handleUpdateCommitteeMember = async (memberData) => {
    try {
      await aproposService.updateCommitteeMember(memberData);
      setAproposData(prev => ({
        ...prev,
        committeeMembers: prev.committeeMembers.map(member =>
          member.id === memberData.id ? { ...member, ...memberData } : member
        )
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating committee member:', error);
    }
  };

  const handleDeleteEventDetail = async (id) => {
    try {
      await aproposService.deleteEventDetail(id);
      setAproposData(prev => ({
        ...prev,
        eventDetails: prev.eventDetails.filter(detail => detail.id !== id)
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error deleting event detail:', error);
    }
  };

  const handleDeleteCommitteeMember = async (id) => {
    try {
      await aproposService.deleteCommitteeMember(id);
      setAproposData(prev => ({
        ...prev,
        committeeMembers: prev.committeeMembers.filter(member => member.id !== id)
      }));
      showSuccessMessage();
    } catch (error) {
      console.error('Error deleting committee member:', error);
    }
  };

  const handleMoveUp = async (memberId) => {
    const section = SECTIONS.find(s => s.key === activeSection);
    if (!section) return;
    try {
      const members = (aproposData.committeeMembers || [])
        .filter(m => m[section.flag])
        .sort((a, b) => (a[section.orderCol] || 0) - (b[section.orderCol] || 0));
      const idx = members.findIndex(m => m.id === memberId);
      if (idx <= 0) return;

      const above = members[idx - 1];
      const current = members[idx];
      const newOrderAbove = current[section.orderCol] ?? idx;
      const newOrderCurrent = above[section.orderCol] ?? idx - 1;

      // Swap the two values
      setAproposData(prev => ({
        ...prev,
        committeeMembers: prev.committeeMembers.map(m => {
          if (m.id === above.id) return { ...m, [section.orderCol]: newOrderAbove };
          if (m.id === current.id) return { ...m, [section.orderCol]: newOrderCurrent };
          return m;
        })
      }));

      if (!isDatabaseEmpty) {
        await aproposService.updateMemberSectionOrder(above.id, newOrderAbove, section.flag);
        await aproposService.updateMemberSectionOrder(current.id, newOrderCurrent, section.flag);
        showSuccessMessage();
      } else {
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error('Error moving member up:', error);
    }
  };

  const handleMoveDown = async (memberId) => {
    const section = SECTIONS.find(s => s.key === activeSection);
    if (!section) return;
    try {
      const members = (aproposData.committeeMembers || [])
        .filter(m => m[section.flag])
        .sort((a, b) => (a[section.orderCol] || 0) - (b[section.orderCol] || 0));
      const idx = members.findIndex(m => m.id === memberId);
      if (idx < 0 || idx >= members.length - 1) return;

      const below = members[idx + 1];
      const current = members[idx];
      const newOrderBelow = current[section.orderCol] ?? idx;
      const newOrderCurrent = below[section.orderCol] ?? idx + 1;

      setAproposData(prev => ({
        ...prev,
        committeeMembers: prev.committeeMembers.map(m => {
          if (m.id === below.id) return { ...m, [section.orderCol]: newOrderBelow };
          if (m.id === current.id) return { ...m, [section.orderCol]: newOrderCurrent };
          return m;
        })
      }));

      if (!isDatabaseEmpty) {
        await aproposService.updateMemberSectionOrder(below.id, newOrderBelow, section.flag);
        await aproposService.updateMemberSectionOrder(current.id, newOrderCurrent, section.flag);
        showSuccessMessage();
      } else {
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error('Error moving member down:', error);
    }
  };

  const handleSectionImageUpload = async (e, imageKey) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const editionYear = selectedEdition;
        const filename = `apropos-${imageKey}-${Date.now()}-${file.name}`;
        const filePath = `${editionYear}/apropos/${filename}`;
        
        const { error } = await supabase
          .storage
          .from('speaker-images')
          .upload(filePath, file, { upsert: true });

        if (error) {
          if (error.message && error.message.includes('bucket does not exist')) {
            alert("Erreur: le bucket 'speaker-images' n'existe pas.");
            return;
          }
          throw error;
        }

        const { data: publicUrlData } = supabase
          .storage
          .from('speaker-images')
          .getPublicUrl(filePath);

        const publicUrl = publicUrlData.publicUrl;
        
        setAproposData(prev => ({
          ...prev,
          images: {
            ...prev.images,
            [imageKey]: publicUrl
          }
        }));
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Erreur lors du téléchargement de l\'image.');
      }
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données APropos est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder dans la BD" pour initialiser les données.</p>
            {hasUnsavedChanges && (
              <button onClick={handleSaveToDatabase} className="btn-primary save-to-db-btn">
                Sauvegarder dans la BD
              </button>
            )}
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {!isDatabaseEmpty && hasUnsavedChanges && (
        <div className="unsaved-changes-warning">
          <span>Modifications non sauvegardées</span>
          <button onClick={handleSaveMainData} className="btn-primary save-btn">
            Sauvegarder
          </button>
        </div>
      )}

      {/* Unsaved Changes Warning for Empty DB */}
      {isDatabaseEmpty && hasUnsavedChanges && (
        <div className="unsaved-changes-warning">
          <span>Données saisies non sauvegardées - Cliquez sur "Sauvegarder dans la BD" pour les enregistrer</span>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Page Header */}
      <div className="section-header">
        <h3>En-tête de la Page</h3>
      </div>
      <div className="form-group">
        <label>Titre (Bilingue/Expert):</label>
        <MultiLangInput 
          value={aproposData.pageHeader?.title || ""}
          onChange={(val) => handleUpdatePageHeader(val)}
          placeholder="Titre de la page"
        />
      </div>

      {/* Event Introduction */}
      <div className="section-header">
        <h3>Introduction de l'Événement</h3>
      </div>
      <div className="form-group">
        <label>Paragraphe 1 (Expert):</label>
        <MultiLangInput 
          type="textarea"
          value={aproposData.eventIntro?.paragraph1 || ""}
          onChange={(val) => handleUpdateEventIntro('paragraph1', val)}
          placeholder="Premier paragraphe"
        />
      </div>
      <div className="form-group">
        <label>Paragraphe 2 (Expert):</label>
        <MultiLangInput 
          type="textarea"
          value={aproposData.eventIntro?.paragraph2 || ""}
          onChange={(val) => handleUpdateEventIntro('paragraph2', val)}
          placeholder="Deuxième paragraphe"
        />
      </div>

      <div className="section-header">
        <h3>Images de l'Événement (16:9)</h3>
      </div>
      <div className="form-group" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: '#002b5b' }}>Image 1</h4>
        <div className="form-group">
          <label>Titre de l'image 1:</label>
          <input 
            type="text" 
            value={aproposData.images?.image1_title || ""}
            onChange={(e) => handleUpdateImageTitle('image1_title', e.target.value)}
            placeholder="Entrez le titre pour l'image 1"
          />
        </div>
        <div className="form-group">
          <label>Fichier Image 1:</label>
          {aproposData.images?.image1_url && (
            <div className="image-preview" style={{ marginBottom: '10px' }}>
              <img src={aproposData.images.image1_url} alt="Image 1" style={{ maxWidth: '300px', borderRadius: '4px' }} />
            </div>
          )}
          <input 
            type="file" 
            accept="image/*"
            onChange={(e) => handleSectionImageUpload(e, 'image1_url')}
          />
        </div>
      </div>

      <div className="form-group" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: '#002b5b' }}>Image 2</h4>
        <div className="form-group">
          <label>Titre de l'image 2:</label>
          <input 
            type="text" 
            value={aproposData.images?.image2_title || ""}
            onChange={(e) => handleUpdateImageTitle('image2_title', e.target.value)}
            placeholder="Entrez le titre pour l'image 2"
          />
        </div>
        <div className="form-group">
          <label>Fichier Image 2:</label>
          {aproposData.images?.image2_url && (
            <div className="image-preview" style={{ marginBottom: '10px' }}>
              <img src={aproposData.images.image2_url} alt="Image 2" style={{ maxWidth: '300px', borderRadius: '4px' }} />
            </div>
          )}
          <input 
            type="file" 
            accept="image/*"
            onChange={(e) => handleSectionImageUpload(e, 'image2_url')}
          />
        </div>
      </div>

      <div className="form-group" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: '#002b5b' }}>Image 3</h4>
        <div className="form-group">
          <label>Titre de l'image 3:</label>
          <input 
            type="text" 
            value={aproposData.images?.image3_title || ""}
            onChange={(e) => handleUpdateImageTitle('image3_title', e.target.value)}
            placeholder="Entrez le titre pour l'image 3"
          />
        </div>
        <div className="form-group">
          <label>Fichier Image 3:</label>
          {aproposData.images?.image3_url && (
            <div className="image-preview" style={{ marginBottom: '10px' }}>
              <img src={aproposData.images.image3_url} alt="Image 3" style={{ maxWidth: '300px', borderRadius: '4px' }} />
            </div>
          )}
          <input 
            type="file" 
            accept="image/*"
            onChange={(e) => handleSectionImageUpload(e, 'image3_url')}
          />
        </div>
      </div>

      {/* Committee Section */}
      <div className="section-header">
        <h3>Section Comité</h3>
      </div>
      <div className="form-group">
        <label>Titre de la section:</label>
        <input 
          type="text" 
          placeholder="COMITE D'ORGANISATION DU CONGRES"
          value={aproposData.committeeSection?.title || ""}
          onChange={(e) => handleUpdateCommitteeSection(e.target.value)}
        />
      </div>

      {/* Event Details Management */}
      <div className="section-header">
        <h3>Gestion des Détails de l'Événement</h3>
        <button className="add-btn" onClick={handleAddDetail}>
          <HiPlus /> Ajouter un détail
        </button>
      </div>

      <div className="card">
        <DataTable 
          value={aproposData.eventDetails || []} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun détail trouvé."
        >
          <Column 
            field="icon" 
            header="Icône" 
            style={{ width: '1%', textAlign: 'center' }} 
            body={(rowData) => <div style={{ fontSize: '1.2em' }}>{rowData.icon}</div>}
          />
          <Column 
            field="title" 
            header="Titre" 
            sortable 
            body={(rowData) => <ExpandableText text={rowData.title} maxChars={30} />}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="content" 
            header="Contenu" 
            body={(rowData) => <ExpandableText text={rowData.content} maxChars={50} />}
            style={{ minWidth: '200px' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <div className="flex gap-2">
                <Button 
                  icon="pi pi-pencil" 
                  rounded 
                  outlined 
                  severity="info" 
                  onClick={() => handleEditDetail(rowData)} 
                  size="small"
                />
                <Button 
                  icon="pi pi-trash" 
                  rounded 
                  outlined 
                  severity="danger" 
                  onClick={() => handleDeleteDetail(rowData.id)} 
                  size="small"
                />
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>

      {/* Committee Members Management */}
      <div className="section-header">
        <h3>Gestion des Membres du Comité</h3>
        <button className="add-btn" onClick={handleAddMember}>
          <HiPlus /> Ajouter un membre
        </button>
      </div>

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {SECTIONS.map(s => {
          const count = (aproposData.committeeMembers || []).filter(m => m[s.flag]).length;
          return (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              style={{
                padding: '7px 18px',
                borderRadius: '20px',
                border: '2px solid',
                borderColor: activeSection === s.key ? '#002b5b' : '#ddd',
                backgroundColor: activeSection === s.key ? '#002b5b' : 'white',
                color: activeSection === s.key ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeSection === s.key ? '600' : '400',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
            >
              {s.label} <span style={{ opacity: 0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>

      <div className="card">
        {(() => {
          const section = SECTIONS.find(s => s.key === activeSection);
          if (!section) return null;
          const filteredMembers = (aproposData.committeeMembers || [])
            .filter(m => m[section.flag])
            .sort((a, b) => (a[section.orderCol] || 0) - (b[section.orderCol] || 0));
          return (
            <DataTable 
              value={filteredMembers} 
              className="p-datatable-sm"
              stripedRows
              showGridlines
              responsiveLayout="scroll"
              tableStyle={{ tableLayout: 'auto' }}
              emptyMessage={`Aucun membre dans la catégorie "${section.label}".`}
            >
              <Column 
                header="Ordre" 
                body={(rowData, options) => (
                  <div className="order-controls">
                    <span className="order-number">{options.rowIndex + 1}</span>
                    <div className="order-buttons">
                      <button 
                        onClick={() => handleMoveUp(rowData.id)}
                        disabled={options.rowIndex === 0}
                        className="order-btn"
                      >
                        <HiChevronUp />
                      </button>
                      <button 
                        onClick={() => handleMoveDown(rowData.id)}
                        disabled={options.rowIndex === filteredMembers.length - 1}
                        className="order-btn"
                      >
                        <HiChevronDown />
                      </button>
                    </div>
                  </div>
                )}
                style={{ width: '1%', whiteSpace: 'nowrap' }}
              />
              <Column 
                field="name" 
                header="Nom" 
                sortable 
                style={{ whiteSpace: 'nowrap' }}
              />
              <Column 
                field="title" 
                header="Titre" 
                sortable 
                body={(rowData) => <ExpandableText text={rowData.title} maxChars={40} />}
                style={{ minWidth: '150px' }}
              />
              <Column 
                header="Sections" 
                body={(rowData) => (
                  <div className="flex flex-wrap gap-1">
                    {rowData.is_president && <Tag value="Président" style={{ background: '#002b5b' }} />}
                    {rowData.is_scientific && <Tag value="Scientifique" severity="info" />}
                    {rowData.is_honorary && <Tag value="Honneur" severity="secondary" />}
                    {rowData.is_organizing && <Tag value="Organisation" style={{ background: '#222' }} />}
                  </div>
                )}
                style={{ minWidth: '120px' }}
              />
              <Column 
                field="organization" 
                header="Organisation" 
                sortable 
                body={(rowData) => <ExpandableText text={rowData.organization} maxChars={40} />}
                style={{ minWidth: '150px' }}
              />
              <Column 
                header="Actions" 
                body={(rowData) => (
                  <div className="flex gap-2">
                    <Button 
                      icon="pi pi-pencil" 
                      rounded 
                      outlined 
                      severity="info" 
                      onClick={() => handleEditMember(rowData)} 
                      size="small"
                    />
                    <Button 
                      icon="pi pi-trash" 
                      rounded 
                      outlined 
                      severity="danger" 
                      onClick={() => handleDeleteMember(rowData.id)} 
                      size="small"
                    />
                  </div>
                )}
                style={{ width: '1%', whiteSpace: 'nowrap' }}
              />
            </DataTable>
          );
        })()}
      </div>

      {/* Member Modal */}
      {showModal && (
        <MemberModal
          mode={modalMode}
          member={selectedMember}
          onSave={(memberData) => {
            if (modalMode === 'add') {
              handleAddCommitteeMember(memberData);
            } else {
              handleUpdateCommitteeMember(memberData);
            }
            closeModal();
          }}
          onClose={closeModal}
        />
      )}

      {/* Event Detail Modal */}
      {showDetailModal && (
        <EventDetailModal
          mode={detailModalMode}
          detail={selectedDetail}
          onSave={(detailData) => {
            if (detailModalMode === 'add') {
              handleAddEventDetail(detailData);
            } else {
              handleUpdateEventDetail(detailData);
            }
            closeDetailModal();
          }}
          onClose={closeDetailModal}
          iconOptions={iconOptions}
        />
      )}
    </div>
  );
}

function MemberModal({ mode, member, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    title: member?.title || '',
    organization: member?.organization || '',
    image: member?.image || '../images/profile.png',
    logo: member?.logo || '',
    is_honorary: member?.is_honorary || false,
    is_organizing: member?.is_organizing || false,
    is_president: member?.is_president || false,
    is_scientific: member?.is_scientific || false
  });
  const [imagePreview, setImagePreview] = useState(member?.image || '../images/profile.png');
  const [logoPreview, setLogoPreview] = useState(member?.logo || '');
  const [uploading, setUploading] = useState(false);
  const [personSearch, setPersonSearch] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [isSearchingPersons, setIsSearchingPersons] = useState(false);

  const handlePersonSearch = async (query) => {
    setPersonSearch(query);
    if (query.length < 2) {
      setPersonResults([]);
      return;
    }
    try {
      setIsSearchingPersons(true);
      const results = await speakersService.getPersons(query);
      setPersonResults(results);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingPersons(false);
    }
  };

  const selectPerson = (person) => {
    setFormData({
      ...formData,
      person_id: person.id,
      name: person.name,
      title: person.title || '',
      organization: person.organization || '',
      image: person.image_url || '../images/profile.png',
      logo: person.company_logo_url || ''
    });
    setImagePreview(person.image_url || '../images/profile.png');
    setLogoPreview(person.company_logo_url || '');
    setPersonResults([]);
    setPersonSearch('');
  };

  // Compress image function
  const compressImage = (file, maxWidth = 300, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setUploading(true);
        // Use same bucket and folder as speakers management
        const editionYear = new Date().getFullYear(); // or pass selectedEdition if available
        const filename = `committee-${Date.now()}-${file.name}`;
        const filePath = `${editionYear}/committee/${filename}`;
        const { data, error } = await supabase
          .storage
          .from('speaker-images')
          .upload(filePath, file, { upsert: true });
        if (error) throw error;

        // Get public URL
        const { data: publicUrlData } = supabase
          .storage
          .from('speaker-images')
          .getPublicUrl(filePath);

        const publicUrl = publicUrlData.publicUrl;
        setFormData({ ...formData, image: publicUrl });
        setImagePreview(publicUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Erreur lors du téléchargement de l\'image.');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setUploading(true);
        // Use same bucket and folder as speakers management
        const editionYear = new Date().getFullYear(); // or pass selectedEdition if available
        const filename = `company-${Date.now()}-${file.name}`;
        const filePath = `${editionYear}/companies/${filename}`;
        const { data, error } = await supabase
          .storage
          .from('speaker-images')
          .upload(filePath, file, { upsert: true });
        if (error) {
          if (error.message && error.message.includes('bucket does not exist')) {
            alert("Erreur: le bucket 'speaker-images' n'existe pas dans Supabase Storage. Veuillez le créer dans la console Supabase.");
            setUploading(false);
            return;
          }
          throw error;
        }

        const { data: publicUrlData, error: publicUrlError } = supabase
          .storage
          .from('speaker-images')
          .getPublicUrl(filePath);

        if (publicUrlError) {
          alert("Erreur lors de la récupération de l'URL publique du logo.");
          setUploading(false);
          return;
        }

        const publicUrl = publicUrlData.publicUrl;
        setFormData({ ...formData, logo: publicUrl });
        setLogoPreview(publicUrl);
      } catch (error) {
        console.error('Error uploading logo:', error);
        alert('Erreur lors du téléchargement du logo.');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Remove data size check
    // const dataSize = JSON.stringify(formData).length;
    // if (dataSize > 30000) { // ~22KB per member
    //   alert('Les données sont trop volumineuses. Veuillez réduire la taille de l\'image.');
    //   return;
    // }
    
    const memberData = mode === 'edit' ? { ...formData, id: member.id } : formData;
    onSave(memberData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter un Membre' : 'Modifier le Membre'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        
        <div className="person-search-section" style={{ padding: '20px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #eee' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
            Rechercher un membre existant (évite les doublons) :
          </label>
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Tapez un nom..." 
              value={personSearch}
              onChange={(e) => handlePersonSearch(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
            {isSearchingPersons && <div style={{ position: 'absolute', right: '10px', top: '10px' }}>Chargement...</div>}
            
            {personResults.length > 0 && (
              <div style={{ 
                position: 'absolute', top: '100%', left: 0, width: '100%', 
                backgroundColor: 'white', border: '1px solid #ddd', zIndex: 10,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto'
              }}>
                {personResults.map(p => (
                  <div 
                    key={p.id} 
                    onClick={() => selectPerson(p)}
                    style={{ padding: '10px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                    onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseOut={e => e.target.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: 'bold' }}>{p.name}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>{p.title} - {p.organization}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Nom complet</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Entrez le nom complet"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Titre/Position</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Entrez le titre ou position"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Organisation:</label>
              <input
                type="text"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder="Entrez l'organisation"
              />
            </div>
         
         <div className="form-group">
           <label>Sections Comité (un membre peut être dans plusieurs):</label>
           <div className="checkbox-group" style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
             <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal' }}>
               <input
                 type="checkbox"
                 checked={formData.is_president}
                 onChange={(e) => setFormData({ ...formData, is_president: e.target.checked })}
               />
               Président du Congrès
             </label>
             <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal' }}>
               <input
                 type="checkbox"
                 checked={formData.is_honorary}
                 onChange={(e) => setFormData({ ...formData, is_honorary: e.target.checked })}
               />
               Comité d'Honneur
             </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.is_scientific}
                  onChange={(e) => setFormData({ ...formData, is_scientific: e.target.checked })}
                />
                Comité Scientifique
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.is_organizing}
                  onChange={(e) => setFormData({ ...formData, is_organizing: e.target.checked })}
                />
                Comité d'Organisation
              </label>
            </div>
          </div>

            <div className="form-group">
              <label>Photo de profil</label>
              <div className="image-upload-container">
                <div className="image-preview-container">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="image-preview"
                  />
                </div>
                <div className="upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="image-upload"
                    className="file-input"
                    disabled={uploading}
                  />
                  <label htmlFor="image-upload" className="upload-btn">
                    {uploading ? "Téléchargement..." : "Choisir une image"}
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Logo de l'organisation</label>
              <div className="image-upload-container">
                <div className="image-preview-container">
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="image-preview"
                      style={{ maxWidth: 80, maxHeight: 80, background: '#fff', border: '1px solid #eee' }}
                    />
                  )}
                </div>
                <div className="upload-controls">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    id="logo-upload"
                    className="file-input"
                    disabled={uploading}
                  />
                  <label htmlFor="logo-upload" className="upload-btn">
                    {uploading ? "Téléchargement..." : "Choisir un logo"}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'add' ? 'Ajouter' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EventDetailModal({ mode, detail, onSave, onClose, iconOptions }) {
  const [formData, setFormData] = useState({
    title: detail?.title || '',
    content: detail?.content || '',
    icon: detail?.icon || iconOptions[0].value,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const detailData = mode === 'edit' ? { ...formData, id: detail.id } : formData;
    onSave(detailData);
  };

  const IconComponent = iconOptions.find(opt => opt.value === formData.icon)?.component;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter un Détail' : 'Modifier le Détail'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-group">
              <label>Titre</label>
              <input 
                type="text" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Entrez le titre"
                required
              />
            </div>

            <div className="form-group">
              <label>Contenu</label>
              <textarea 
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Entrez le contenu. Utilisez des sauts de ligne pour plusieurs lignes."
                required
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Icône</label>
              <div className="icon-select-container">
                <select 
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="icon-select"
                >
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="icon-preview">
                  {IconComponent}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'add' ? 'Ajouter' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AProposManagement;

